import { useCallback, useEffect, useMemo, useState } from "react";

import {
  ActivateConnectionSettingsDocument,
  ActivateConnectionSettingsQuery,
  EltConnectionSettingsDocument,
  EltConnectionSettingsQuery,
} from "@/apollo/types";
import { ApolloQueryResult, useApolloClient } from "@apollo/client";

import { useLatestValueRef } from "./useLatestValueRef";

export type ConnectionSettingsType = {
  required: boolean;
  loading: boolean;
  value: any;
  refetch: (
    formValues?: Record<string, any>,
    forceFetch?: boolean,
  ) => Promise<void>;
  error: string | undefined;
};

export default function useConnectionSettings(
  id: string,
  connectionType: "ELT" | "Activate",
  formValuesProp?: Record<string, any>,
): ConnectionSettingsType {
  const client = useApolloClient();
  const [value, setValue] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const formValuesRef = useLatestValueRef(formValuesProp);

  const handleFetchSettings = useCallback(
    async (formValues?: Record<string, any>, forceFetch: boolean = false) => {
      const ConnectionSettingsDocument =
        connectionType === "ELT"
          ? EltConnectionSettingsDocument
          : ActivateConnectionSettingsDocument;

      setLoading(true);
      setError(undefined);
      try {
        const {
          data,
        }: ApolloQueryResult<
          EltConnectionSettingsQuery & ActivateConnectionSettingsQuery
        > = await client.query({
          fetchPolicy: forceFetch ? "network-only" : "cache-first",
          query: ConnectionSettingsDocument,
          variables: {
            input: {
              id,
              formValues: {
                ...formValuesRef.current,
                ...formValues,
              },
            },
          },
        });
        setLoading(false);
        if (connectionType === "ELT") {
          setValue(data.getEltSettings.connectionSettings);
        } else if (connectionType === "Activate") {
          setValue(data.getActivateSettings.connectionSettings);
        } else {
          throw new Error(
            `useConnectionSettings - Connection type: (${connectionType}) is invalid`,
          );
        }
      } catch (err: any) {
        setLoading(false);
        setError(err?.message ?? err);
      }
    },
    [client, id, connectionType, formValuesRef],
  );
  useEffect(() => {
    if (!id) return;
    // Force a network fetch on mount. `getEltSettings`/`getActivateSettings`
    // can carry ephemeral, server-issued state (e.g. a freshly minted
    // webhookId for the webhook form) that must not be silently served from
    // Apollo's cache when this step is entered again — a cache-first read
    // here can return a previous session's now-invalid response with no
    // network request ever being made.
    handleFetchSettings(undefined, true);
  }, [handleFetchSettings, id]);

  const required = !!Object.keys(value || {}).length;

  const object = useMemo(
    () => ({
      required,
      loading,
      value,
      refetch: handleFetchSettings,
      error,
    }),
    [error, handleFetchSettings, loading, required, value],
  );
  return object;
}
